import React, { useEffect, useState } from 'react';
import { Badge, Box, Button, Drawer, Typography } from '@mui/material';
import useUser from 'stores/useCustomer';

import { motion } from 'framer-motion';
import { useServerTranslation } from 'hooks/useServerTranslation';
import Totals from './components/Totals';
import CartItemList from './components/CartItemList/CartItemList';
import CheckoutButton from './components/CheckoutButton';
import { colors } from '../../styles/theme/colors';
import { CartIcon } from './components/CartIcon';

export default function Cart() {
    const cart = useUser((state) => state.cart);

    const [isCartOpen, setIsCartOpen] = useState(false);

    const { t } = useServerTranslation();

    const toggleCartDrawer = () => setIsCartOpen((prevState) => !prevState);

    if (!cart) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <CartButton cartDrawer={toggleCartDrawer} itemCount={0} />
            </Box>
        );
    }

    const cartItems = cart?.cart.items || [];

    const currentItemCount = cartItems
        .map((item) => item.quantity)
        .reduce((total, qty) => total + qty, 0);

    return (
        <>
            <CartButton cartDrawer={toggleCartDrawer} itemCount={currentItemCount} />

            <Drawer
                open={isCartOpen}
                anchor="right"
                onClose={toggleCartDrawer}
                PaperProps={{ sx: { maxWidth: '90vw', width: 375, padding: 2 } }}
            >
                <Box display="inline-flex" justifyContent="space-between">
                    <Typography variant="h1" marginBottom={2}>
                        {t('checkout.sub-title')}
                    </Typography>
                    <Typography>
                        {currentItemCount}
                        {' '}
                        {t('shopping-cart.totals.product-items')}
                    </Typography>
                </Box>
                <Box display="flex" flexDirection="column" height="100%">
                    <CartItemList
                        items={cartItems}
                    />
                    <Totals
                        sx={{ marginBottom: 2 }}
                        totals={cart.totals.rows || []}
                    />
                    <CheckoutButton />
                    <Button
                        onClick={toggleCartDrawer}
                    >
                        {t('common.button.return-to-product-page').toUpperCase()}
                    </Button>
                </Box>
            </Drawer>
        </>
    );
}

type CartButtonProps = {
    itemCount?: number;
    cartDrawer(): void;
};

function CartButton({ itemCount, cartDrawer }:CartButtonProps) {
    const [isAnimating, setIsAnimating] = useState<boolean>(false);
    const [itemCountState, setItemCountState] = useState(itemCount);
    const { t } = useServerTranslation();

    useEffect(() => {
        const timeout = setTimeout(() => {
            setIsAnimating(false);
        }, 300);
        if ((itemCount && itemCountState !== undefined) && itemCount > itemCountState) {
            setIsAnimating(true);

            setItemCountState(itemCount);
        }
        setItemCountState(itemCount);

        return () => clearTimeout(timeout);
    }, [itemCount, itemCountState]);

    return (
        <Button
            variant="black"
            size="small"
            onClick={cartDrawer}
            sx={{
                borderRadius: { xs: '50%', md: '2px' },
                marginRight: 1,
                padding: '8px',
                '&:hover': { backgroundColor: { md: colors.white }, color: { md: colors.pink2 } },
            }}
        >
            <Typography sx={{
                display: { xs: 'none', md: 'inline-block' },
                flexDirection: 'row',
                alignItems: 'center',
                marginRight: 1.5,
            }}
            >
                {t('menu.cart')}
            </Typography>
            <Badge
                sx={{
                    '& .MuiBadge-badge': {
                        right: -3,
                        top: 0,
                    },
                }}
                badgeContent={itemCount}
                color="primary"
            >
                <motion.div
                    animate={{ scale: isAnimating ? 2 : 1 }}
                    transition={{ duration: 0.5 }}
                    style={{ display: 'flex', alignItems: 'center' }}
                >
                    <CartIcon />
                </motion.div>
            </Badge>
        </Button>
    );
}
